import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Card } from '@/components/ui/card';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState({});

  const galleryImages = [
    {
      url: 'https://images.unsplash.com/photo-1666214277730-e9c7e755e5a3?w=800&q=80',
      caption: 'Patient consultation',
      alt: 'Doctor consulting with patient'
    },
    {
      url: 'https://images.unsplash.com/photo-1704455306925-1401c3012117?w=800&q=80',
      caption: 'Modern hospital room',
      alt: 'Hospital room with medical equipment'
    },
    {
      url: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=800&q=80',
      caption: 'Diagnostic equipment',
      alt: 'Medical diagnostic tools'
    },
    {
      url: 'https://images.unsplash.com/photo-1704455306251-b4634215d98f?w=800&q=80',
      caption: 'Treatment room',
      alt: 'Medical treatment room interior'
    },
    {
      url: 'https://images.unsplash.com/photo-1758691462126-2ee47c8bf9e7?w=800&q=80',
      caption: 'Compassionate care',
      alt: 'Doctor with mother and child'
    },
    {
      url: 'https://images.unsplash.com/photo-1633488781325-d36e6818d0c8?w=800&q=80',
      caption: 'Patient monitoring',
      alt: 'ICU patient examination'
    },
    {
      url: 'https://images.pexels.com/photos/8436598/pexels-photo-8436598.jpeg?w=800&q=80',
      caption: 'Community outreach',
      alt: 'Community health outreach program'
    },
    {
      url: 'https://images.unsplash.com/photo-1763070282912-08b63e2eb427?w=800&q=80',
      caption: 'Cardiology services',
      alt: 'Stethoscope for cardiac care'
    },
    {
      url: 'https://images.pexels.com/photos/287237/pexels-photo-287237.jpeg?w=800&q=80',
      caption: 'Medical instruments',
      alt: 'Professional medical equipment'
    },
  ];

  const handleImageLoad = (index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  };

  return (
    <main className="py-12 bg-gradient-to-br from-blue-50 to-cyan-50 min-h-screen" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Photo Gallery
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            A glimpse into our charitable healthcare mission serving the KGF community
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryImages.map((image, index) => (
            <Card
              key={index}
              className="overflow-hidden cursor-pointer card-hover group"
              onClick={() => setSelectedImage(image)}
              data-testid={`gallery-image-${index}`}
            >
              <div className="relative aspect-[4/3] bg-gray-200">
                {/* Loading placeholder */}
                {!imageLoaded[index] && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="spinner"></div>
                  </div>
                )}
                
                {/* Image */}
                <img
                  src={image.url}
                  alt={image.alt}
                  loading="lazy"
                  onLoad={() => handleImageLoad(index)}
                  className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${
                    imageLoaded[index] ? 'opacity-100' : 'opacity-0'
                  }`}
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-semibold text-lg">{image.caption}</p>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox-modal"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedImage(null)}
            data-testid="close-lightbox-button"
            aria-label="Close lightbox"
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.alt}
              className="w-full h-auto rounded-lg shadow-2xl"
            />
            <p className="text-white text-center mt-4 text-xl font-semibold">
              {selectedImage.caption}
            </p>
          </div>
        </div>
      )}
    </main>
  );
};

export default Gallery;
